import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { S, C } from "../ui";
import { TRADES } from "../constants";
import { TYPE_LABEL } from "./IncidentsPanel";

// An incident open (unresolved) longer than this is a genuine outlier
// worth surfacing — not a precise SLA, just long enough that a same-day
// incident isn't flagged while it's still fresh.
const STALE_MS = 3 * 24 * 60 * 60 * 1000;

// Only shown when a Facility Admin owns more than one active building
// (see FacilityAdminDashboard.jsx) — a single-building admin has no use
// for a cross-building view. Firestore's `in` operator caps at 30
// values, so this doesn't paginate past a 30-building portfolio; fine
// for now, revisit if a real portfolio owner ever gets that large.
function useAcrossBuildings(collectionName, facilityIds) {
  const [docs, setDocs] = useState([]);
  const key = facilityIds.join(",");
  useEffect(() => {
    if (facilityIds.length === 0) { setDocs([]); return; }
    return onSnapshot(query(collection(db, collectionName), where("facilityId", "in", facilityIds)), s =>
      setDocs(s.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [collectionName, key]);
  return docs;
}

function tsToDateKey(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toISOString().slice(0, 10);
}
function tsToLabel(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

// Same resolution ReportPanel.jsx uses (contract's own snapshotted
// vendorName, not a live join) — just scoped to one building's own
// contracts, passed in already pre-filtered by the caller.
function resolveVendorName(vendorId, dateKey, contracts) {
  if (!vendorId) return "Internal Staff";
  const active = contracts.find(c => c.vendorId === vendorId && c.startDate <= dateKey && (!c.endDate || dateKey <= c.endDate));
  if (active?.vendorName) return active.vendorName;
  const any = contracts.find(c => c.vendorId === vendorId);
  return any?.vendorName || "Unknown vendor";
}

// facilities: the owner's active buildings (myFacilities from
// FacilityAdminDashboard.jsx) — removed buildings are deliberately
// excluded from both the overview and the report, same as everywhere
// else a portfolio owner's building list shows up.
// onNavigate (optional): (facilityId, tab, trade?) => void — lets a
// coverage-matrix cell or a "needs attention" item jump straight to that
// building's Vendors tab (see FacilityAdminDashboard.jsx's
// jumpFromPortfolio). Guarded with `?.` everywhere so this component still
// works standalone if ever rendered without it.
export default function PortfolioPanel({ facilities, onNavigate }) {
  const [mode, setMode] = useState("overview");
  const facilityIds = facilities.map(f => f.id);
  const byId = Object.fromEntries(facilities.map(f => [f.id, f]));

  const vendors = useAcrossBuildings("vendors", facilityIds);
  const workers = useAcrossBuildings("workers", facilityIds);
  const assets = useAcrossBuildings("assets", facilityIds);
  const incidents = useAcrossBuildings("incidentFlags", facilityIds);
  const completions = useAcrossBuildings("checklistCompletions", facilityIds);
  const contracts = useAcrossBuildings("vendorContracts", facilityIds);

  // Only trades actually used somewhere in the portfolio become matrix
  // columns — this is what makes "missing at building X" a real gap
  // (covered elsewhere in the portfolio) rather than flagging every trade
  // nobody here has ever hired for.
  const tradesInUse = TRADES.filter(t => vendors.some(v => v.trade === t));
  const cellVendors = (facilityId, trade) => vendors.filter(v => v.facilityId === facilityId && v.trade === trade);
  const gapItems = facilities.flatMap(f =>
    tradesInUse.filter(t => cellVendors(f.id, t).length === 0).map(t => ({ facility: f, trade: t }))
  );

  const msOf = (ts) => (ts?.toDate ? ts.toDate().getTime() : new Date(ts).getTime());
  const now = Date.now();
  const staleIncidents = incidents
    .filter(i => !i.resolved && i.timestamp && now - msOf(i.timestamp) > STALE_MS)
    .sort((a, b) => msOf(a.timestamp) - msOf(b.timestamp));

  const totalStaff = workers.filter(w => w.roleType === "internal_staff").length;
  const openIncidentsCount = incidents.filter(i => !i.resolved).length;
  const perBuildingCompletion = facilities.map(f => {
    const items = completions.filter(c => c.facilityId === f.id);
    if (!items.length) return null;
    return items.reduce((sum, x) => {
      const it = x.items || [];
      return sum + (it.length ? it.filter(y => y.done).length / it.length : 0);
    }, 0) / items.length;
  }).filter(x => x !== null);
  const avgCompletion = perBuildingCompletion.length
    ? Math.round(perBuildingCompletion.reduce((a, b) => a + b, 0) / perBuildingCompletion.length * 100)
    : null;

  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(monthAgo);
  const [endDate, setEndDate] = useState(today);
  const [generated, setGenerated] = useState(false);
  const inRange = (dateKey) => dateKey >= startDate && dateKey <= endDate;

  return (
    <div>
      <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: `1px solid ${C.border}` }}>
        {[["overview", "Overview"], ["report", "Report"]].map(([id, label]) => (
          <div key={id} onClick={() => setMode(id)}
            style={{ padding: "10px 14px", cursor: "pointer", fontSize: 13, fontWeight: 600, color: mode === id ? C.accent : C.muted, borderBottom: mode === id ? `2px solid ${C.accent}` : "2px solid transparent" }}>
            {label}
          </div>
        ))}
      </div>

      {mode === "overview" && (
        <div>
          <div className="portfolio-stat-strip" style={{ ...S.statStrip, background: C.white, border: `1px solid ${C.border}`, marginBottom: 24 }}>
            <div style={S.stat}><div style={S.statLabel}>Buildings</div><div style={S.statValue}>{facilities.length}</div></div>
            <div style={S.stat}><div style={S.statLabel}>Vendors</div><div style={S.statValue}>{vendors.length}</div></div>
            <div style={S.stat}><div style={S.statLabel}>Internal Staff</div><div style={S.statValue}>{totalStaff}</div></div>
            <div style={S.stat}><div style={S.statLabel}>Assets</div><div style={S.statValue}>{assets.length}</div></div>
            <div style={S.stat}><div style={S.statLabel}>Avg Completion</div><div style={S.statValue}>{avgCompletion ?? "—"}{avgCompletion !== null ? "%" : ""}</div></div>
            <div style={S.stat}><div style={S.statLabel}>Open Incidents</div><div style={{ ...S.statValue, color: openIncidentsCount > 0 ? C.red : C.ink }}>{openIncidentsCount}</div></div>
            <div style={S.stat}><div style={S.statLabel}>Coverage Gaps</div><div style={{ ...S.statValue, color: gapItems.length > 0 ? C.attention : C.ink }}>{gapItems.length}</div></div>
          </div>

          <div className="two-col-grid" style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 20, alignItems: "start" }}>
            <div style={{ background: C.white, border: `1px solid ${C.border}`, overflow: "hidden" }}>
              <div style={{ padding: "14px 16px", borderBottom: `1px solid ${C.border}` }}>
                <div style={{ fontWeight: 700 }}>Trade coverage</div>
                <div style={{ fontSize: 12, color: C.muted }}>Which vendor covers each trade, per building</div>
              </div>
              {tradesInUse.length === 0 ? (
                <div style={{ padding: 24, textAlign: "center", color: C.muted, fontSize: 13 }}>No vendors added yet — add vendors under each building's Vendors tab to see coverage here.</div>
              ) : (
                <div className="portfolio-matrix-scroll">
                  <table className="portfolio-matrix">
                    <thead>
                      <tr>
                        <th className="matrix-sticky-col" style={S.tableHead}>Building</th>
                        {tradesInUse.map(t => <th key={t} style={S.tableHead}>{t}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {facilities.map(f => (
                        <tr key={f.id}>
                          <td className="matrix-sticky-col" style={S.tableCell}>
                            <div style={{ fontWeight: 600 }}>{f.name}</div>
                            <div style={{ fontSize: 11, color: C.muted }}>{f.address}</div>
                          </td>
                          {tradesInUse.map(t => {
                            const list = cellVendors(f.id, t);
                            return (
                              <td key={t} style={S.tableCell}>
                                <span
                                  title={list.map(v => v.companyName).join(", ")}
                                  onClick={() => onNavigate?.(f.id, "vendors", t)}
                                  style={{ ...S.tag(list.length ? "success" : "danger"), cursor: onNavigate ? "pointer" : "default" }}
                                >
                                  {list.length ? (list.length === 1 ? list[0].companyName : `${list[0].companyName} +${list.length - 1}`) : "Gap"}
                                </span>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div style={{ background: C.white, border: `1px solid ${C.border}`, padding: 16 }}>
              <div style={{ fontWeight: 700, marginBottom: 12 }}>Needs attention</div>

              <div style={{ ...S.kicker, marginBottom: 8 }}>Open incidents (3+ days)</div>
              {staleIncidents.length === 0 && <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>Nothing overdue.</div>}
              {staleIncidents.slice(0, 8).map(i => {
                const f = byId[i.facilityId];
                const days = Math.floor((now - msOf(i.timestamp)) / 86400000);
                const fContracts = contracts.filter(c => c.facilityId === i.facilityId);
                const vendorName = resolveVendorName(i.vendorId, tsToDateKey(i.timestamp), fContracts);
                return (
                  <div key={i.id} onClick={() => onNavigate?.(i.facilityId, "incidents")}
                    style={{ padding: "8px 0", borderBottom: `1px solid ${C.border}`, cursor: onNavigate ? "pointer" : "default", fontSize: 12 }}>
                    <div style={{ fontWeight: 600 }}>{TYPE_LABEL[i.type] || i.type} — {vendorName}</div>
                    <div style={{ color: C.muted }}>{f?.name} · open {days}d</div>
                  </div>
                );
              })}
              {staleIncidents.length > 8 && <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>+{staleIncidents.length - 8} more</div>}

              <div style={{ ...S.kicker, margin: "16px 0 8px" }}>Coverage gaps</div>
              {gapItems.length === 0 && <div style={{ fontSize: 12, color: C.muted }}>No coverage gaps.</div>}
              {gapItems.slice(0, 8).map((g, idx) => (
                <div key={idx} onClick={() => onNavigate?.(g.facility.id, "vendors", g.trade)}
                  style={{ padding: "8px 0", borderBottom: `1px solid ${C.border}`, cursor: onNavigate ? "pointer" : "default", fontSize: 12 }}>
                  <div style={{ fontWeight: 600 }}>{g.trade}</div>
                  <div style={{ color: C.muted }}>missing at {g.facility.name}</div>
                </div>
              ))}
              {gapItems.length > 8 && <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>+{gapItems.length - 8} more</div>}
            </div>
          </div>
        </div>
      )}

      {mode === "report" && (
        <div>
          <div className="portfolio-report-controls" style={{ display: "flex", gap: 10, alignItems: "flex-end", marginBottom: 20, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Start date</div>
              <input type="date" style={{ ...S.input, marginBottom: 0 }} value={startDate} onChange={e => setStartDate(e.target.value)} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>End date</div>
              <input type="date" style={{ ...S.input, marginBottom: 0 }} value={endDate} onChange={e => setEndDate(e.target.value)} />
            </div>
            <button style={{ ...S.btn(), width: "auto", padding: "10px 18px" }} onClick={() => setGenerated(true)}>Generate Report</button>
            {generated && <button style={{ ...S.ghostBtn, width: "auto", padding: "10px 18px" }} onClick={() => window.print()}>Print / Save as PDF</button>}
          </div>

          {generated && (
            <div className="portfolio-report-output">
              <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 2 }}>Portfolio Compliance Report</div>
              <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>{facilities.length} buildings · {startDate} to {endDate}</div>

              {facilities.map(f => {
                const fContracts = contracts.filter(c => c.facilityId === f.id);
                const events = [
                  ...completions.filter(c => c.facilityId === f.id).map(c => ({ kind: "Checklist", dateKey: tsToDateKey(c.timestamp), when: tsToLabel(c.timestamp), vendorId: c.vendorId, detail: `${c.items?.filter(i => i.done).length ?? 0}/${c.items?.length ?? 0} items completed` })),
                  ...incidents.filter(i => i.facilityId === f.id).map(i => ({ kind: "Incident", dateKey: tsToDateKey(i.timestamp), when: tsToLabel(i.timestamp), vendorId: i.vendorId, detail: `${i.type}: ${i.description}${i.resolved ? " (resolved)" : " (open)"}` })),
                ].filter(e => inRange(e.dateKey)).sort((a, b) => a.dateKey.localeCompare(b.dateKey));

                return (
                  <div key={f.id} style={{ background: C.white, borderRadius: 0, border: `1px solid ${C.border}`, padding: 20, marginBottom: 16 }}>
                    <div style={{ fontWeight: 700, marginBottom: 2 }}>{f.name}</div>
                    <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>{f.address} · {events.length} events</div>
                    {events.length === 0 && <div style={{ fontSize: 13, color: C.muted }}>No activity in this date range.</div>}
                    {events.map((e, idx) => (
                      <div key={idx} style={{ padding: "10px 0", borderBottom: idx === events.length - 1 ? "none" : `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                        <div>
                          <span style={S.pill(C.tintAccent, C.accent)}>{e.kind}</span>{" "}
                          <b>{resolveVendorName(e.vendorId, e.dateKey, fContracts)}</b> — {e.detail}
                        </div>
                        <div style={{ color: C.muted }}>{e.when}</div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <style>{`
        @media print {
          body * { visibility: hidden; }
          .portfolio-report-output, .portfolio-report-output * { visibility: visible; }
          .portfolio-report-output { position: absolute; top: 0; left: 0; width: 100%; }
          .portfolio-report-controls { display: none !important; }
        }
      `}</style>
    </div>
  );
}
